/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// UNO CLEAR Arabic bundle-page localization bridge.
// Scoped to Arabic + the custom bundle section only. This keeps the existing
// bundle/cart mechanics untouched while replacing legacy hardcoded English UI.
function initUnoArabicBundleLocalization() {
  const lang = (document.documentElement.lang || '').toLowerCase();
  if (!lang.startsWith('ar')) return;

  const roots = document.querySelectorAll('.uno-bundle');
  if (!roots.length) return;

  const setText = (el, value) => {
    if (el && el.textContent !== value) el.textContent = value;
  };
  const setHtml = (el, value) => {
    if (el && el.innerHTML !== value) el.innerHTML = value;
  };
  const setAttr = (el, name, value) => {
    if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
  };
  const setTrailingText = (el, value) => {
    if (!el) return;
    const textNode = Array.from(el.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (textNode && textNode.textContent.trim() !== value) textNode.textContent = ` ${value}`;
  };
  const extractNumber = (text) => String(text || '').match(/\d+(?:[.,]\d+)?/)?.[0] || '';
  const localizeSavePercent = (text) => {
    const value = extractNumber(text);
    return value ? `وفر ${value}%` : '';
  };
  const localizeSavingAmount = (text) => {
    const source = String(text || '').trim();
    if (!source) return '';
    const amount = source.replace(/^You save\s*/i, '').replace(/^وفرت\s*/i, '');
    return amount ? `وفرت ${amount}` : '';
  };

  roots.forEach((root) => {
    let queued = false;
    const localize = () => {
      queued = false;

      const badge = root.querySelector('[data-save-percent]');
      if (badge && badge.textContent.trim()) setText(badge, localizeSavePercent(badge.textContent));

      setAttr(root.querySelector('.uno-bundle__thumbs'), 'aria-label', 'صور وفيديوهات المنتج');
      root.querySelectorAll('[data-thumb-id]').forEach((thumb, index) => setAttr(thumb, 'aria-label', `عرض الوسائط ${index + 1}`));

      const desktopBenefits = root.querySelectorAll('.uno-bundle__desktop-benefits .uno-bundle__benefit');
      ['منتجات UNO CLEAR أصلية', 'قيمة أفضل في روتين واحد', 'توصيل سريع لجميع أنحاء مصر'].forEach((value, index) => {
        setText(desktopBenefits[index]?.querySelector('span:last-child'), value);
      });

      setText(root.querySelector('.uno-bundle__eyebrow'), 'باقة UNO CLEAR');
      setText(root.querySelector('.uno-bundle__rating-text'), 'عناية متكاملة. قيمة أفضل.');

      const saving = root.querySelector('[data-saving-text]');
      if (saving && saving.textContent.trim()) setText(saving, localizeSavingAmount(saving.textContent));

      const highlights = root.querySelectorAll('.uno-bundle__highlight-strip > div');
      setText(highlights[0]?.querySelector('strong'), 'روتين مختار بعناية');
      setText(highlights[0]?.querySelector('span'), 'منتجات مختارة لتكمل بعضها');
      setText(highlights[1]?.querySelector('strong'), 'عرض لفترة محدودة');
      setText(highlights[1]?.querySelector('span'), 'سعر الباقة متاح حتى نفاد الكمية');

      setAttr(root.querySelector('[data-qty-minus]'), 'aria-label', 'تقليل الكمية');
      setAttr(root.querySelector('[data-qty-input]'), 'aria-label', 'الكمية');
      setAttr(root.querySelector('[data-qty-plus]'), 'aria-label', 'زيادة الكمية');

      const addButton = root.querySelector('.uno-bundle__add');
      setText(addButton?.querySelector('span:first-child'), addButton?.disabled ? 'نفد المخزون' : 'أضف الباقة إلى السلة');
      setTrailingText(root.querySelector('.uno-bundle__payment-note'), 'دفع آمن · الدفع عند الاستلام متاح');

      const mobileBenefits = root.querySelectorAll('.uno-bundle__mobile-benefits > div');
      ['منتجات أصلية', 'قيمة أفضل للباقة', 'توصيل لجميع أنحاء مصر'].forEach((value, index) => setTrailingText(mobileBenefits[index], value));

      const details = root.querySelector('.uno-bundle__details-heading');
      setText(details?.querySelector(':scope > span'), 'داخل الباقة');
      setText(details?.querySelector('h2'), 'كل ما يحتاجه روتينك.');
      setText(details?.querySelector('p'), 'مجموعة مختارة بعناية من UNO CLEAR بسعر باقة أفضل.');

      const promiseCopy = root.querySelector('.uno-bundle__promise-copy');
      setText(promiseCopy?.querySelector(':scope > span'), 'وعد UNO CLEAR');
      setHtml(promiseCopy?.querySelector('h2'), 'الأولى في العناية.<br>الأولى في الثقة.');

      const promiseItems = root.querySelectorAll('.uno-bundle__promise-grid > div');
      const promiseCopyItems = [
        ['تركيبات مدروسة', 'منتجات عناية شخصية مصممة للاستخدام اليومي المريح.'],
        ['روتين أبسط', 'أساسيات متكاملة مجمعة في عملية شراء واحدة سهلة.'],
        ['قيمة استثنائية', 'منتجات أكثر من اللي بتستخدمها مع توفير حقيقي في الباقة.'],
      ];
      promiseCopyItems.forEach(([title, body], index) => {
        setText(promiseItems[index]?.querySelector('strong'), title);
        setText(promiseItems[index]?.querySelector('p'), body);
      });

      const relatedHead = root.querySelector('.uno-bundle__related-head');
      setText(relatedHead?.querySelector('span'), 'اكتشف المزيد');
      setText(relatedHead?.querySelector('a'), 'عرض كل الباقات ←');

      const stickyPrice = root.querySelector('[data-sticky-price]');
      if (stickyPrice && /(?:Save|وفر)\s*\d+(?:[.,]\d+)?%/i.test(stickyPrice.textContent)) {
        const percent = extractNumber(stickyPrice.textContent.split('·').pop());
        const base = stickyPrice.textContent.split('·')[0].trim();
        setText(stickyPrice, `${base} · وفر ${percent}%`);
      }

      const stickyButton = root.querySelector('[data-sticky-add]');
      setText(stickyButton, stickyButton?.disabled ? 'نفد المخزون' : 'أضف إلى السلة');
    };

    const schedule = () => {
      if (queued) return;
      queued = true;
      queueMicrotask(localize);
    };

    localize();
    const observer = new MutationObserver(schedule);
    observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
  });
}

// This file is deferred, so the DOM is normally complete when it executes.
// Keep the fallback for unusual script ordering in previews/apps.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUnoArabicBundleLocalization, { once: true });
} else {
  initUnoArabicBundleLocalization();
}
